export const DEFAULT_INPUT = {
  monthlyInvestment: 5000,
  durationYears: 10,
  annualReturn: 12,
  stepUpPercent: 20,
  frequency: "6_MONTHS"
};

export const INPUT_LIMITS = {
  monthlyInvestment: {min: 100, max: 5000000},
  durationYears: {min: 1, max: 50},
  annualReturn: {min: 1, max: 12, step: 0.5},
  stepUpPercent: {min: 0, max: 100, step: 1}
};

export const FREQUENCY_OPTIONS = [
  {label: "6 months", value: "6_MONTHS"},
  {label: "1 year", value: "1_YEAR"}
];

export const investmentSteps = [
  100, 500, 1000, 1500, 2000, 3000, 5000, 10000, 15000, 20000, 25000,
  30000, 40000, 50000, 75000, 100000, 125000, 150000, 175000, 200000,
  225000, 250000, 275000, 300000, 325000, 350000, 375000, 400000, 425000,
  450000, 475000, 500000, 525000, 550000, 575000, 600000, 625000, 650000,
  675000, 700000, 725000, 750000, 775000, 800000, 825000, 850000, 875000,
  900000, 925000, 950000, 975000, 1000000, 1250000, 1500000, 1750000,
  2000000, 2500000, 3000000, 3500000, 4000000, 4500000, 5000000
];

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const roundToStep = (value, step) => Math.round(value / step) * step;

const roundCurrency = (value) => Math.round(value);

const normalizeInput = (input) => ({
  monthlyInvestment: clamp(
    Math.round(input.monthlyInvestment),
    INPUT_LIMITS.monthlyInvestment.min,
    INPUT_LIMITS.monthlyInvestment.max
  ),
  durationYears: clamp(
    Math.round(input.durationYears),
    INPUT_LIMITS.durationYears.min,
    INPUT_LIMITS.durationYears.max
  ),
  annualReturn: clamp(
    roundToStep(input.annualReturn, INPUT_LIMITS.annualReturn.step),
    INPUT_LIMITS.annualReturn.min,
    INPUT_LIMITS.annualReturn.max
  ),
  stepUpPercent: clamp(
    roundToStep(input.stepUpPercent, INPUT_LIMITS.stepUpPercent.step),
    INPUT_LIMITS.stepUpPercent.min,
    INPUT_LIMITS.stepUpPercent.max
  ),
  frequency: input.frequency
});

export const getMonthlyRate = (annualReturn) =>
  Math.pow(1 + annualReturn / 100, 1 / 12) - 1;

export const getCompletedIntervals = (month, frequency) => {
  if (frequency === "1_YEAR") {
    return Math.floor((month - 1) / 12);
  }
  return Math.floor((month - 1) / 6);
};

export const getCurrentSip = (initialSip, stepUpPercent, completedIntervals) =>
  initialSip * Math.pow(1 + stepUpPercent / 100, completedIntervals);

export const calculateSip = (input) => {
  const {
    monthlyInvestment,
    durationYears,
    annualReturn,
    stepUpPercent,
    frequency
  } = normalizeInput(input);

  const totalMonths = durationYears * 12;
  const monthlyRate = getMonthlyRate(annualReturn);

  let portfolio = 0;
  let totalInvested = 0;
  const yearlyData = [];

  for (let month = 1; month <= totalMonths; month += 1) {
    const completedIntervals = getCompletedIntervals(month, frequency);
    const currentSip = getCurrentSip(
      monthlyInvestment,
      stepUpPercent,
      completedIntervals
    );

    totalInvested += currentSip;
    portfolio += currentSip;
    portfolio *= 1 + monthlyRate;

    if (month % 12 === 0) {
      const displayTotalInvested = roundCurrency(totalInvested);
      const displayPortfolioValue = roundCurrency(portfolio);
      const displayGain = roundCurrency(portfolio - totalInvested);

      yearlyData.push({
        year: month / 12,
        totalInvested: displayTotalInvested,
        portfolioValue: displayPortfolioValue,
        gain: displayGain
      });
    }
  }

  const displayTotalInvested = roundCurrency(totalInvested);
  const displayFutureValue = roundCurrency(portfolio);
  const displayGain = roundCurrency(portfolio - totalInvested);
  const gainPercent =
    displayTotalInvested === 0 ? 0 : (displayGain / displayTotalInvested) * 100;

  return {
    futureValue: displayFutureValue,
    totalInvested: displayTotalInvested,
    gain: displayGain,
    gainPercent,
    yearlyData
  };
};

export const formatCurrency = (value) =>
  `₹${roundCurrency(value).toLocaleString("en-IN")}`;

export const formatGainCurrency = (value) => {
  const amount = roundCurrency(value);
  const prefix = amount >= 0 ? "+ " : "- ";
  return `${prefix}${formatCurrency(Math.abs(amount))}`;
};

export const formatPercent = (value, fractionDigits = 2) =>
  `${value.toFixed(fractionDigits)}%`;

export const findClosestStepValue = (value) =>
  investmentSteps.reduce((prev, curr) =>
    Math.abs(curr - value) < Math.abs(prev - value) ? curr : prev
  );
